import { api } from './api';

export interface RiskConfig {
  totalBudget: number;
  maxPositionSize: number;
  maxPerTrade: number;
  dailyLossLimit: number;
  maxPositions: number;
  maxConcentrationPct: number;
  requireConfirmationAbove: number;
  isActive: boolean;
}

export interface RiskUtilization {
  budgetUsed: number;
  budgetTotal: number;
  budgetUtilizationPct: number;
  positionsCount: number;
  positionsMax: number;
  dailyLossCurrent: number;
  dailyLossLimit: number;
}

export interface RiskCheckResult {
  passed: boolean;
  violations: Array<{
    rule: string;
    message: string;
    severity: 'warning' | 'block';
    currentValue: number;
    limitValue: number;
  }>;
  requiresConfirmation: boolean;
}

export const riskService = {
  async getConfig(): Promise<RiskConfig> {
    const res = await api.get<Record<string, unknown>>('/risk/config');
    return {
      totalBudget: Number(res.total_budget ?? 0),
      maxPositionSize: Number(res.max_position_size ?? 0),
      maxPerTrade: Number(res.max_per_trade ?? 0),
      dailyLossLimit: Number(res.daily_loss_limit ?? 0),
      maxPositions: Number(res.max_positions ?? 0),
      maxConcentrationPct: Number(res.max_concentration_pct ?? 0),
      requireConfirmationAbove: Number(res.require_confirmation_above ?? 0),
      isActive: Boolean(res.is_active),
    };
  },

  async updateConfig(config: Partial<RiskConfig>): Promise<RiskConfig> {
    // Convert camelCase to snake_case for the backend
    const payload: Record<string, unknown> = {};
    if (config.totalBudget !== undefined) payload.total_budget = config.totalBudget;
    if (config.maxPositionSize !== undefined) payload.max_position_size = config.maxPositionSize;
    if (config.maxPerTrade !== undefined) payload.max_per_trade = config.maxPerTrade;
    if (config.dailyLossLimit !== undefined) payload.daily_loss_limit = config.dailyLossLimit;
    if (config.maxPositions !== undefined) payload.max_positions = config.maxPositions;
    if (config.maxConcentrationPct !== undefined)
      payload.max_concentration_pct = config.maxConcentrationPct;
    if (config.requireConfirmationAbove !== undefined)
      payload.require_confirmation_above = config.requireConfirmationAbove;
    if (config.isActive !== undefined) payload.is_active = config.isActive;

    const res = await api.put<Record<string, unknown>>('/risk/config', payload);
    return {
      totalBudget: Number(res.total_budget ?? 0),
      maxPositionSize: Number(res.max_position_size ?? 0),
      maxPerTrade: Number(res.max_per_trade ?? 0),
      dailyLossLimit: Number(res.daily_loss_limit ?? 0),
      maxPositions: Number(res.max_positions ?? 0),
      maxConcentrationPct: Number(res.max_concentration_pct ?? 0),
      requireConfirmationAbove: Number(res.require_confirmation_above ?? 0),
      isActive: Boolean(res.is_active),
    };
  },

  async getUtilization(): Promise<RiskUtilization> {
    const res = await api.get<Record<string, unknown>>('/risk/status');
    return {
      budgetUsed: Number(res.budget_used ?? 0),
      budgetTotal: Number(res.budget_total ?? 0),
      budgetUtilizationPct: Number(res.budget_utilization_pct ?? 0),
      positionsCount: Number(res.positions_count ?? 0),
      positionsMax: Number(res.positions_max ?? 0),
      dailyLossCurrent: Number(res.daily_loss_current ?? 0),
      dailyLossLimit: Number(res.daily_loss_limit ?? 0),
    };
  },

  async validateOrder(order: {
    symbol: string;
    quantity: number;
    price: number;
    side: 'buy' | 'sell';
  }): Promise<RiskCheckResult> {
    const res = await api.post<Record<string, unknown>>('/risk/validate', order);
    const violations = (res.violations as Array<Record<string, unknown>>) || [];
    return {
      passed: Boolean(res.passed),
      violations: violations.map((v) => ({
        rule: String(v.rule ?? ''),
        message: String(v.message ?? ''),
        severity: (v.severity as 'warning' | 'block') ?? 'warning',
        currentValue: Number(v.current_value ?? 0),
        limitValue: Number(v.limit_value ?? 0),
      })),
      requiresConfirmation: Boolean(res.requires_confirmation),
    };
  },
};

export default riskService;
